import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  exportWorkflowsByApprovers,
  loadWorkflowsByApprovers,
} from '../../../actions/reportActions';
import Gravatar from 'react-gravatar';
import { Table, SimpleTextFilter, TableColumn, getVisibleColumns } from '../../common/Table';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import WorkflowLink from '../../common/links/WorkflowLink';
import UserLink from '../../common/links/UserLink';
import { Trans, useTranslation } from 'react-i18next';
import CompanyLink from '../../common/links/CompanyLink';

export interface WorkflowsByApproversListProps {
  currentUser?: any;
}

function WorkflowsByApproversList({ currentUser }: WorkflowsByApproversListProps) {
  const { t } = useTranslation();
  const allColumns = [
    'keyWorkflowName',
    'fullName',
    'companyName',
    'groupName',
    'emailAddress',
  ];
  const location = useLocation();
  const defaultVisibleColumns = allColumns;
  const [reload] = useState(undefined);
  const [items, setItems] = useState<any>();
  const params = new URLSearchParams(location.search);

  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'workflowsbyapproversreport-settings',
      getTableSettingsDefaults({
        defaultSort: 'keyWorkflowName',
        defaultVisibleColumns,
      }),
      null,
      loadWorkflowsByApprovers,
      setItems,
      reload,
      t('Failed to load report')
    );

  let columnsConfig: TableColumn[] = [
    {
      Key: 'keyWorkflowName',
      Label: t('Workflow name'),
      Render: (item: any) => (
        <WorkflowLink
          id={item.keyWorkflowId}
          name={item.keyWorkflowName}
          colour={item.keyWorkflowColourHex}
        />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'groupName',
      Label: t('Group membership'),
      Render: (item: any) =>
        item.userProfileGroupId && (
          <div>
            <Trans i18nKey="memberOfGroup" values={{ groupName: item.groupName }}>
              Member of{' '}
              <NavLink to={`/settings/user-profile-group/${item.userProfileGroupId}`}>
                {item.groupName}
              </NavLink>
            </Trans>
          </div>
        ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'emailAddress',
      Label: t('Email'),
      Render: (item: any) => item.emailAddress,
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'companyName',
      Label: t('Company'),
      Render: (row: any) => (
        <div className="p-2 d-flex align-items-center company-td">
          <CompanyLink
            id={row.companyId}
            name={row.companyName}
            imageId={row.companyLogoImageId}
            imageSize={32}
            colourHex={row.companyColourHex}
          />
        </div>
      ),
    },
    {
      Key: 'fullName',
      Label: t('Name'),
      Render: (item: any) => (
        <div style={{ display: 'flex' }}>
          <Gravatar
            email={item?.emailAddress}
            size={20}
            rating="pg"
            default="retro"
            className="img-circle me-3"
          />
          <UserLink
            id={item.userProfileId}
            name={item.fullName}
            imageSize={32}
          />
        </div>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return items ? (
    <KwPanel className="mb-4">
      <Table
        className="table-v-stripes"
        columns={columns}
        allColumns={allColumns}
        scopes={items.scopes}
        rows={items.data.map((x: any) => ({
          Key: `${x.keyWorkflowId}-${x.userProfileGroupId}-${x.userProfileId}`,
          ...x,
        }))}
        totalRowCount={items.totalRowCount}
        filteredRowCount={items.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportWorkflowsByApprovers}
      />
    </KwPanel>
  ) : (
    <Loading></Loading>
  );
}

export default WorkflowsByApproversList;
